var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DeepClone } from "../util.js";
export function CreateEngineWorker(workerUrl, relativeEngineModuleDir, relativeEngineDataFile, name) {
    const worker = new Worker(workerUrl, { name });
    worker.postMessage([relativeEngineModuleDir, relativeEngineDataFile, name]);
    let msgId = 0;
    let engineReadyResolver;
    const engineReady = new Promise(resolve => (engineReadyResolver = resolve));
    const waitingMessages = new Map();
    worker.addEventListener("message", ev => {
        const msg = ev.data;
        switch (msg.type) {
            case "engineInitialized":
                engineReadyResolver();
                return;
            default:
                break;
        }
        const callback = waitingMessages.get(msg.id);
        if (callback) {
            waitingMessages.delete(msg.id);
            callback(msg);
        }
    });
    function Destroy() {
        worker.terminate();
    }
    function SendMessage(msg_1) {
        return __awaiter(this, arguments, void 0, function* (msg, transfer = []) {
            const response = yield new Promise(resolve => {
                waitingMessages.set(msg.id, resolve);
                worker.postMessage(msg, transfer);
            });
            return response;
        });
    }
    // creates a function that cannot be called multiple times simultaneously, e.g. for insole generation:
    // start the generation, and if any more generation requests are received, only keep the latest
    // when the generation is finished, run the latest (repeat the process above if another request is received in the meantime)
    function CreateThrottler(fn) {
        let inProgress = false;
        let waitingResolver = null;
        return (...params) => __awaiter(this, void 0, void 0, function* () {
            if (inProgress) {
                // if already waiting, then replace previous waiter
                waitingResolver === null || waitingResolver === void 0 ? void 0 : waitingResolver(false);
                const canProceed = yield new Promise(resolve => (waitingResolver = resolve));
                if (!canProceed) {
                    return null;
                }
            }
            inProgress = true;
            const result = yield fn(...params);
            inProgress = false;
            waitingResolver === null || waitingResolver === void 0 ? void 0 : waitingResolver(true);
            return result;
        });
    }
    const insoleGenerationThrottler = CreateThrottler((msg) => __awaiter(this, void 0, void 0, function* () {
        return yield SendMessage(msg);
    }));
    function GetEngineInfo() {
        return __awaiter(this, void 0, void 0, function* () {
            const { versionMajor, versionMinor, versionPatch, extraInfo, commitHash } = yield yield SendSimpleMessage("getEngineInfo", {});
            return { versionMajor, versionMinor, versionPatch, extraInfo, commitHash };
        });
    }
    function ConfigureLog(name, error, warning, info, debug) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield SendSimpleMessage("configureLog", { name, error, warning, info, debug });
        });
    }
    function ClearLogs() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield SendSimpleMessage("clearLogs", {});
        });
    }
    function GenerateInsole(insoleParameters, exportParameters, meshGenerationParameters, alignFootScan, calculateMeasurements, isExport) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = msgId++;
            insoleParameters = DeepClone(insoleParameters);
            exportParameters = DeepClone(exportParameters);
            meshGenerationParameters = DeepClone(meshGenerationParameters);
            yield engineReady;
            const msg = {
                type: "generateInsole",
                id,
                isExport,
                insoleParameters,
                exportParameters,
                meshGenerationParameters,
                alignFootScan,
                calculateMeasurements,
            };
            if (isExport) {
                const exportedMesh = (yield SendMessage(msg)).mesh;
                return {
                    isExport: true,
                    exportedMesh,
                };
            }
            else {
                const result = yield insoleGenerationThrottler(msg);
                return result === null
                    ? null
                    : {
                        isExport: false,
                        result,
                    };
            }
        });
    }
    function SendSimpleMessage(type_1, msg_1) {
        return __awaiter(this, arguments, void 0, function* (type, msg, transfer = []) {
            const id = msgId++;
            return yield SendMessage(Object.assign(Object.assign({}, msg), { type, id }), transfer);
        });
    }
    function Fence() {
        return __awaiter(this, void 0, void 0, function* () {
            // Ensures that all engine operations queued before this call are finished before returning
            yield SendSimpleMessage("fence", {});
        });
    }
    function SetEnabledTemplateCategories(categoryMask) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("setEnabledTemplateCategories", { categoryMask });
        });
    }
    function GetTemplates() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getTemplates", {})).templates;
        });
    }
    function SetTemplate(nameOrIndex) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("setTemplate", {
                template: typeof nameOrIndex === "string"
                    ? { type: "name", name: nameOrIndex }
                    : { type: "index", index: nameOrIndex },
            });
        });
    }
    function GetDefaultInsoleParameters() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getDefaultInsoleParameters", {})).parameters;
        });
    }
    function GetInsoleParameters() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getInsoleParameters", {})).parameters;
        });
    }
    function SetDefaultInsoleParameters() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("setDefaultInsoleParameters", {})).parameters;
        });
    }
    const raycastSurfaceThrottler = CreateThrottler((msg) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        return (_a = (yield SendMessage(msg)).hit) !== null && _a !== void 0 ? _a : false;
    }));
    function RaycastSurface(mode, rayOrigin, rayDirection, colliderResolutionU, colliderResolutionV) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = msgId++;
            const msg = {
                type: "raycastSurface",
                id,
                mode,
                rayOrigin: DeepClone(rayOrigin),
                rayDirection: DeepClone(rayDirection),
                colliderResolutionU,
                colliderResolutionV,
            };
            const result = yield raycastSurfaceThrottler(msg);
            if (result === null) {
                return { type: "throttled" };
            }
            else if (result) {
                return { type: "hit", point: result[0], uv: result[1] };
            }
            else {
                return { type: "noHit" };
            }
        });
    }
    function ImportMesh(format, fileBytes, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { meshData, ptr, originalBBoxCenter } = yield SendSimpleMessage("importMesh", {
                format,
                fileBytes,
                options,
            });
            return { meshData, ptr, originalBBoxCenter };
        });
    }
    function ExportMesh(format, mesh, zoneMeshesPtr, zoneMeshesCount) {
        return __awaiter(this, void 0, void 0, function* () {
            if (format === "3mf") {
                return (yield SendSimpleMessage("exportMesh3mf", {
                    meshPtr: mesh.nativePtr,
                    zoneMeshesPtr,
                    zoneMeshesCount,
                })).fileBytes;
            }
            else {
                return (yield SendSimpleMessage("exportMesh", {
                    format,
                    meshPtr: mesh.nativePtr,
                })).fileBytes;
            }
        });
    }
    function GetZoneMeshes() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield SendSimpleMessage("getZoneMeshes", {});
        });
    }
    function GetZoneMesh(index) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield SendSimpleMessage("getZoneMesh", { index });
        });
    }
    function DestroyMesh(meshPtr) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("destroyMesh", { meshPtr });
        });
    }
    function SetTwistCorrectionBehaviour(params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("setTwistCorrectionBehaviour", params);
        });
    }
    function SetFootscanAlignmentBehaviour(params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("setFootscanAlignmentBehaviour", params);
        });
    }
    function SetFootScan(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("setFootScan", params)).alignedPose;
        });
    }
    function IsFootScanValid() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("isFootScanValid", {})).valid;
        });
    }
    function GetFootScanMeasurements() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getFootScanMeasurements", {})).measurements;
        });
    }
    function GetFootScanPCA() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getFootScanPCA", {})).pcaData;
        });
    }
    function GetHeelPostingPostPlane() {
        return __awaiter(this, void 0, void 0, function* () {
            const { normal, tangent } = yield SendSimpleMessage("getHeelPostingPostPlane", {});
            return { normal, tangent };
        });
    }
    function GetCalculatedRegionBasedWidthLines() {
        return __awaiter(this, void 0, void 0, function* () {
            const { proximalLineStart, proximalLineEnd, distalLineStart, distalLineEnd } = yield SendSimpleMessage("getCalculatedRegionBasedWidthLines", {});
            return { proximalLineStart, proximalLineEnd, distalLineStart, distalLineEnd };
        });
    }
    function GetCalculatedRegionBasedWidthPoints() {
        return __awaiter(this, void 0, void 0, function* () {
            const { proximalLineStart, proximalLineEnd, distalLineStart, distalLineEnd, midMedial, midLateral } = yield SendSimpleMessage("getCalculatedRegionBasedWidthPoints", {});
            return { proximalLineStart, proximalLineEnd, distalLineStart, distalLineEnd, midMedial, midLateral };
        });
    }
    function GetMetatarsalPadContourLine(metatarsalPadType) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getMetatarsalPadContourLine", { metatarsalPadType })).vertices;
        });
    }
    function AlignFootScan() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("alignFootScan", {})).alignedPose;
        });
    }
    function GetFootScanAlignedPose() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getFootscanAlignedPose", {})).alignedPose;
        });
    }
    function GetFootscanLandmarks() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getFootscanLandmarks", {})).footscanLandmarks;
        });
    }
    function GetFootscanLandmarkTypes() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getFootscanLandmarkTypes", {})).footscanLandmarkTypes;
        });
    }
    function FindClosestPointToFootscan(point) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("findClosestPointToFootscan", { point })).closestPoint;
        });
    }
    function SetFootscanLandmarks(side, footscanType, landmarks, mainAxis, backmostPoint) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("setFootscanLandmarks", {
                side,
                footscanType,
                landmarks,
                mainAxis,
                backmostPoint,
            });
        });
    }
    function SetFootscanLandmark(...landmarks) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("setFootscanLandmark", { landmarks });
        });
    }
    function FinalizeLandmarks(calculateDependentLandmarks) {
        return __awaiter(this, void 0, void 0, function* () {
            const { landmarks, measurements } = yield SendSimpleMessage("finalizeLandmarks", { calculateDependentLandmarks });
            return { landmarks, measurements };
        });
    }
    function GetFittingInsoleMeasurements() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getFittingInsoleMeasurements", {})).result;
        });
    }
    function SelectBestFitInsole(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("selectBestFitInsole", params)).result;
        });
    }
    function MeshCalculateBoundingBox(meshPtrs) {
        return __awaiter(this, void 0, void 0, function* () {
            const { minP, maxP } = yield SendSimpleMessage("meshCalculateBoundingBox", { meshPtrs });
            return { minP, maxP };
        });
    }
    function MeshClone(srcMeshPtr, dstMeshPtr) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("meshClone", { srcMeshPtr, dstMeshPtr }))
                .ptr;
        });
    }
    function MeshTransform(meshPtr, returnTransformedMeshData, translation, rotation, scale) {
        return __awaiter(this, void 0, void 0, function* () {
            const { transformedMeshData } = yield SendSimpleMessage("meshTransform", {
                meshPtr,
                returnTransformedMeshData,
                translation,
                rotation,
                scale,
            });
            return transformedMeshData;
        });
    }
    function MeshGenerateNormals(meshPtr, returnNewMeshData, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { transformedMeshData } = yield SendSimpleMessage("meshGenerateNormals", {
                meshPtr,
                returnNewMeshData,
                params,
            });
            return transformedMeshData;
        });
    }
    function MeshCropWithPlanes(meshPtr, returnNewMeshData, planes) {
        return __awaiter(this, void 0, void 0, function* () {
            const { transformedMeshData } = yield SendSimpleMessage("meshCropWithPlanes", {
                meshPtr,
                returnNewMeshData,
                planes,
            });
            return transformedMeshData;
        });
    }
    function RaycasterCreate(meshPtr_1) {
        return __awaiter(this, arguments, void 0, function* (meshPtr, buildStrategy = 1) {
            return (yield SendSimpleMessage("raycasterCreate", {
                meshPtr,
                buildStrategy,
            })).ptr;
        });
    }
    function RaycasterDestroy(ptr) {
        return __awaiter(this, void 0, void 0, function* () {
            yield SendSimpleMessage("raycasterDestroy", { ptr });
        });
    }
    function RaycasterRaycast(ptr, rayOrigin, rayDirection) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("raycasterRaycast", {
                ptr,
                rayOrigin,
                rayDirection,
            })).tuv;
        });
    }
    function EnablerServiceGetRandomValue() {
        return __awaiter(this, void 0, void 0, function* () {
            const { randomValue } = yield SendSimpleMessage("enablerServiceGetRandomValue", {});
            return randomValue;
        });
    }
    function EnablerServiceVerifyMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const { success } = yield SendSimpleMessage("enablerServiceVerify", { message });
            return success;
        });
    }
    function EnablerServiceBeginExport() {
        return __awaiter(this, void 0, void 0, function* () {
            const { randomValue } = yield SendSimpleMessage("enablerServiceBeginExport", {});
            return randomValue;
        });
    }
    function EnablerServiceExportInsole(insoleParameters, exportParameters, meshGenerationParameters) {
        return __awaiter(this, void 0, void 0, function* () {
            yield engineReady;
            const msg = { insoleParameters, exportParameters, meshGenerationParameters };
            return (yield SendSimpleMessage("enablerServiceExportInsole", msg)).result;
        });
    }
    function EnablerServiceEndExport(message) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("enablerServiceEndExport", { message })).mesh;
        });
    }
    function LoadEmbossedLogoSVG(name, fileContents) {
        return __awaiter(this, void 0, void 0, function* () {
            const { success } = yield SendSimpleMessage("loadEmbossedLogoSVG", { name, fileContents });
            return success;
        });
    }
    function GetLatticeZoneData() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getLatticeZoneData", {})).zoneData;
        });
    }
    function GetZoneData() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield SendSimpleMessage("getZoneData", {})).zoneData;
        });
    }
    return {
        ready: engineReady,
        Destroy,
        GetEngineInfo,
        ConfigureLog,
        ClearLogs,
        GenerateInsole,
        SetEnabledTemplateCategories,
        GetTemplates,
        SetTemplate,
        GetDefaultInsoleParameters,
        GetInsoleParameters,
        SetDefaultInsoleParameters,
        RaycastSurface,
        ImportMesh,
        ExportMesh,
        GetZoneMeshes,
        GetZoneMesh,
        DestroyMesh,
        SetTwistCorrectionBehaviour,
        SetFootscanAlignmentBehaviour,
        SetFootScan,
        IsFootScanValid,
        GetFootScanMeasurements,
        GetFootScanPCA,
        GetHeelPostingPostPlane,
        GetCalculatedRegionBasedWidthLines,
        GetCalculatedRegionBasedWidthPoints,
        GetMetatarsalPadContourLine,
        AlignFootScan,
        GetFootScanAlignedPose,
        GetFootscanLandmarks,
        GetFootscanLandmarkTypes,
        FindClosestPointToFootscan,
        SetFootscanLandmarks,
        SetFootscanLandmark,
        FinalizeLandmarks,
        GetFittingInsoleMeasurements,
        SelectBestFitInsole,
        MeshCalculateBoundingBox,
        MeshClone,
        MeshTransform,
        MeshGenerateNormals,
        MeshCropWithPlanes,
        RaycasterCreate,
        RaycasterDestroy,
        RaycasterRaycast,
        EnablerServiceGetRandomValue,
        EnablerServiceVerifyMessage,
        EnablerServiceBeginExport,
        EnablerServiceExportInsole,
        EnablerServiceEndExport,
        LoadEmbossedLogoSVG,
        GetLatticeZoneData,
        GetZoneData,
        Fence,
    };
}
//# sourceMappingURL=worker-interface.js.map